import React, { useState, useEffect } from "react";
import {
    Container,
    Typography,
    Grid,
    Button,
    Box,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    LinearProgress,
    Card,
    CardContent,
} from "@mui/material";
import { CreditCard } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../context/AuthContext";
import axios from "axios";

function Subscription() {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const [payments, setPayments] = useState([]);
    const [jobCredits, setJobCredits] = useState([]);
    const [applications, setApplications] = useState([]);
    const [starterUsage, setStarterUsage] = useState(null);
    const [professionalUsage, setProfessionalUsage] = useState(null);
    const [plans, setPlans] = useState([]);

    // NEW: holds the same "credits" object AdminDashboard uses for the
    // Purchased Credits card (jobs / applications / call alerts / whatsapp alerts)
    const [enterpriseCredits, setEnterpriseCredits] = useState(null);

    const [loading, setLoading] = useState(true);
    const [freeTrialQuota, setFreeTrialQuota] = useState(null);

    useEffect(() => {

        async function fetchSubscriptionData() {
            try {

                const userResponse =
                    await api.get("/auth/user");

                setUser(userResponse.data);

                // Fetch live free trial quota if user is on free_trial
                if (userResponse.data?.plan === 'free_trial') {
                    try {
                        const quotaRes = await api.get("/payment/free-trial-quota");

                        console.log("USER RESPONSE:", userResponse.data);
                        console.log("FREE TRIAL QUOTA:", quotaRes.data);

                        setFreeTrialQuota(quotaRes.data);
                    } catch (err) {
                        console.log(err);
                    }
                }
                const response =
                    await api.get("/payment/history");

                const creditsResponse =
                    await api.get("/job-credits/my-credits");

                const applicationsResponse =
                    await api.get("/applications?page=1&limit=10000");

                const starterUsageResponse =
                    await api.get("/applications/starter-usage");

                const professionalUsageResponse =
                    await api.get("/applications/professional-usage");

                // NEW: fetch the same dashboard "credits" payload AdminDashboard uses
                // so the Subscription page can render an identical Purchased Credits
                // card for Custom Enterprise customers. Isolated in its own try/catch
                // so a failure here never breaks the rest of the subscription page.
                try {
                    const dashboardRes = await api.get("/analytics/admin/jobs/dashboard");
                    setEnterpriseCredits(dashboardRes.data?.credits || null);
                } catch (err) {
                    console.log("Failed to fetch enterprise credits:", err);
                }




                console.log(
                    "APPLICATION API RESPONSE:",
                    applicationsResponse.data
                );

                console.log(
                    "STARTER USAGE API:",
                    starterUsageResponse.data
                );

                console.log(
                    "PROFESSIONAL USAGE API:",
                    professionalUsageResponse.data
                );






                // Always set usage states regardless of payment history API response shape
                setStarterUsage(starterUsageResponse.data);
                setProfessionalUsage(professionalUsageResponse.data);

                const plansResponse = await api.get("/payment/plans");
                setPlans(plansResponse.data.plans || []);


                if (Array.isArray(response.data)) {

                    setPayments(response.data);

                    if (
                        Array.isArray(creditsResponse.data)
                    ) {

                        setJobCredits(
                            creditsResponse.data
                        );

                    } else {

                        setJobCredits([]);

                    }

                    // ✅ ALWAYS refresh applications separately

                    if (
                        Array.isArray(
                            applicationsResponse.data
                                ?.applications
                        )
                    ) {

                        setApplications(
                            applicationsResponse.data
                                .applications
                        );

                        console.log(
                            "APPLICATIONS:",
                            applicationsResponse.data
                                .applications
                        );

                    } else if (
                        Array.isArray(
                            applicationsResponse.data
                        )
                    ) {

                        setApplications(
                            applicationsResponse.data
                        );

                        console.log(
                            "APPLICATIONS:",
                            applicationsResponse.data
                        );

                    } else {

                        setApplications([]);

                    }


                } else {

                    setPayments([]);

                }

            } catch (error) {

                console.error(
                    "Failed to fetch subscription data:",
                    error
                );

                setPayments([]);

            } finally {

                setLoading(false);

            }
        }

        fetchSubscriptionData();

        const interval =
            setInterval(() => {

                fetchSubscriptionData();

            }, 5000);

        return () => clearInterval(interval);

    }, []);

    const now = new Date();

    const activeCredits = Array.isArray(jobCredits)
        ? jobCredits.filter(
            (credit) =>
                credit &&
                !credit.used_at &&
                credit.expires_at &&
                new Date(credit.expires_at) > now
        )
        : [];

    const latestCredit =
        Array.isArray(jobCredits) &&
        jobCredits.length > 0
            ? [...jobCredits]
                .filter(c => c && c.expires_at && new Date(c.expires_at) > now) // ONLY ACTIVE ONES
                .sort(
                    (a, b) =>
                        new Date(b.created_at) -
                        new Date(a.created_at)
                )[0]
            : null;

    const validCreditsCount =
        activeCredits.length;

    const freePlan = plans.find(p => p.code === 'FREE');

    const PLAN_LIMITS = {
        FREE_TRIAL: {
            jobs: Number(freePlan?.jobLimit ?? 1),
            applications: Number(freePlan?.applicationLimit ?? 5),
            validityDays: 15,
            color: "#6f2dbd",
        },



        STARTER: {
            jobs: starterUsage?.totalJobs || 0,
            applications: starterUsage?.limit || 0,
        },

        PROFESSIONAL: {
            jobs: professionalUsage?.totalJobs || 0,
            applications: professionalUsage?.limit || 0,
        }
    };

    // ===== FIX: Custom Enterprise "is it still the active plan?" check =====
    // total_jobs_purchased / total_applications_purchased are LIFETIME
    // CUMULATIVE counters on the user record — they are set once when
    // enterprise credits are purchased and never reset to 0, even after the
    // account switches to Starter / Professional / Free Trial. Using them
    // alone to decide "current plan" meant Custom Enterprise would show up
    // forever, regardless of what plan was purchased afterward.
    //
    // Two separate things must now both be true for Custom Enterprise to be
    // treated as the *active* plan:
    //   1. hasCustomCreditsPurchased -> the account has enterprise credits on
    //      record at all (still needed for things like the "linked to
    //      Company ID" banner elsewhere in this file)
    //   2. hasRemainingCustomCredits -> there is still unused balance left,
    //      i.e. nothing has been fully consumed/replaced
    // AND, most importantly, that no explicit newer plan (user.plan =
    // monthly/professional/annual) is currently set on the account — that
    // field is the authoritative signal for "what did the user most
    // recently purchase/activate", and must always win over the old
    // cumulative credit counters.
    const hasCustomCreditsPurchased =
        (user?.total_jobs_purchased ?? 0) > 0 ||
        (user?.total_applications_purchased ?? 0) > 0;

    const hasRemainingCustomCredits =
        (user?.remaining_custom_jobs ?? 0) > 0 ||
        (user?.remaining_custom_applications ?? 0) > 0;

    let detectedPlan = null;

    const userPlanLower = user?.plan
        ? user.plan.toLowerCase()
        : null;

    // ✅ PRIORITY 1 → PROFESSIONAL
    // The explicit `user.plan` field set by the backend on purchase/renewal
    // is the authoritative "what's active right now" signal, so it is
    // checked BEFORE the Custom Enterprise credit counters.

    if (
        userPlanLower === "professional" ||
        userPlanLower === "annual"
    ) {
        detectedPlan = "PROFESSIONAL";
    }

    // ✅ PRIORITY 2 → STARTER

    else if (
        userPlanLower === "monthly"
    ) {
        detectedPlan = "STARTER";
    }

        // ✅ PRIORITY 3 → CUSTOM ENTERPRISE
        // Only wins when there's no explicit Starter/Professional plan active
        // AND there are still remaining (unused) custom credits. This is what
        // stops a stale Custom Enterprise view from sticking around after a
    // Starter/Professional plan has been purchased.

    else if (
        hasCustomCreditsPurchased &&
        hasRemainingCustomCredits
    ) {
        detectedPlan = "CUSTOM_ENTERPRISE";
    }

    // ✅ PRIORITY 4 → FALLBACK FROM PAYMENTS

    else if (
        payments.some(
            (p) =>
                p.plan_code
                    ?.toUpperCase()
                    .includes("PROFESSIONAL") &&
                p.expires_at &&
                new Date(p.expires_at) > now
        )
    ) {
        detectedPlan = "PROFESSIONAL";
    }

    else if (
        payments.some(
            (p) =>
                p.plan_code
                    ?.toUpperCase()
                    .includes("STARTER") &&
                p.expires_at &&
                new Date(p.expires_at) > now
        )
    ) {
        detectedPlan = "STARTER";
    }


    let currentPlan = detectedPlan;

    if (detectedPlan) {
        currentPlan = detectedPlan;
    }

    console.log("PAYMENTS:", payments);

    console.log("DETECTED PLAN:", detectedPlan);
    console.log("CURRENT PLAN:", currentPlan);

    const hasActivePaidPlan =
        currentPlan === "STARTER" ||
        currentPlan === "PROFESSIONAL" ||
        currentPlan === "CUSTOM_ENTERPRISE";

    if (!hasActivePaidPlan) {

        if (
            user?.free_trial_expires_at ||
            user?.free_trial_started_at ||
            user?.trial_end_date
        ) {
            currentPlan = "FREE_TRIAL";
        }

    }

    // FIX: the top "Custom Enterprise Credits" banner used to show any time
    // there were leftover custom credits on record, independent of which
    // plan was actually active — so it kept appearing even after the user
    // switched to Starter/Professional. It must mirror the main plan card
    // below: only visible when Custom Enterprise is genuinely the active
    // plan.
    const showsEnterpriseCreditsBanner =
        currentPlan === "CUSTOM_ENTERPRISE";

    const limits = PLAN_LIMITS[currentPlan];

    const jobsLimit =
        currentPlan === "FREE_TRIAL"
            ? PLAN_LIMITS.FREE_TRIAL.jobs
            : limits?.jobs || 0;

    const applicationsLimit =
        currentPlan === "FREE_TRIAL"
            ? PLAN_LIMITS.FREE_TRIAL.applications
            : limits?.applications || 0;



    // REMAINING JOBS
    const remainingJobs =
        currentPlan === "FREE_TRIAL"
            ? Math.max(
                freeTrialQuota?.jobsRemaining ?? user?.job_limit ?? jobsLimit,
                0
            )



            : currentPlan === "STARTER"
                ? Math.max(
                    starterUsage?.remainingJobs ?? starterUsage?.totalJobs ?? 0,
                    0
                )

                : currentPlan === "PROFESSIONAL"
                    ? Math.max(
                        professionalUsage?.remainingJobs ?? professionalUsage?.totalJobs ?? 0,
                        0
                    )

                    : Number(user?.job_limit ?? 0);

    // TOTAL APPLICATIONS
    const totalApplicationsLimit =
        applicationsLimit;

    console.log(
        "LIVE APPLICATIONS:",
        applications
    );





    // REMAINING APPLICATIONS


    const remainingApplications =
        currentPlan === "FREE_TRIAL"
            ? Math.max(
                freeTrialQuota?.applicationsRemaining ?? user?.application_limit ?? applicationsLimit,
                0
            )

            : currentPlan === "STARTER"
                ? Math.max(
                    starterUsage?.remaining ?? starterUsage?.limit ?? 0,
                    0
                )

                : currentPlan === "PROFESSIONAL"
                    ? Math.max(
                        professionalUsage?.remaining ?? professionalUsage?.limit ?? 0,
                        0
                    )

                    : Number(
                        user?.application_limit ??
                        0
                    );

    console.log(
        "REMAINING APPLICATIONS:",
        remainingApplications
    );

    console.log(
        "STARTER USAGE OBJECT:",
        starterUsage
    );

    console.log(
        "STARTER USED:",
        starterUsage?.used
    );

    console.log(
        "STARTER REMAINING:",
        starterUsage?.remaining
    );

    console.log(
        "STARTER LIMIT:",
        starterUsage?.limit
    );

    console.log(
        "FINAL REMAINING JOBS:",
        remainingJobs
    );

    console.log(
        "FINAL REMAINING APPLICATIONS:",
        remainingApplications
    );

    const usageCompleted =
        remainingJobs <= 0 &&
        remainingApplications <= 0;




    let expiryDate = null;

    if (currentPlan === "FREE_TRIAL") {

        expiryDate =
            user?.free_trial_expires_at;

    }



    else if (
        currentPlan === "STARTER"
    ) {

        const starterPayment = Array.isArray(payments) ? payments.find(p =>
            p.plan_code?.toUpperCase().includes("STARTER") &&
            p.expires_at &&
            new Date(p.expires_at) > now
        ) : null;

        expiryDate =
            user?.subscription_expires_at ||
            user?.plan_expires_at ||
            user?.expires_at ||
            starterPayment?.expires_at;

    }

    else if (
        currentPlan === "PROFESSIONAL"
    ) {

        const professionalPayment = Array.isArray(payments) ? payments.find(p =>
            p.plan_code?.toUpperCase().includes("PROFESSIONAL") &&
            p.expires_at &&
            new Date(p.expires_at) > now
        ) : null;

        expiryDate =
            user?.subscription_expires_at ||
            user?.plan_expires_at ||
            user?.expires_at ||
            professionalPayment?.expires_at;

    }

    let remainingDays = 0;

    if (expiryDate) {
        remainingDays = Math.max(
            0,
            Math.ceil(
                (new Date(expiryDate) - now) /
                (1000 * 60 * 60 * 24)
            )
        );
    }

    const isPlanExpired =
        usageCompleted ||
        (
            expiryDate &&
            new Date(expiryDate) < now
        );

    // ===== NEW: derived values for the Custom Enterprise "Purchased Credits" card =====
    // Mirrors exactly how AdminDashboard.jsx computes these same 4 numbers.
    // Falls back to the older user.* fields if the dashboard credits call
    // hasn't loaded yet / fails, so nothing breaks.

    const entTotalJobsPurchased =
        enterpriseCredits?.total_jobs_purchased ?? user?.total_jobs_purchased ?? 0;

    const entTotalApplicationsPurchased =
        enterpriseCredits?.total_applications_purchased ?? user?.total_applications_purchased ?? 0;

    const entTotalCallAlertsPurchased =
        enterpriseCredits?.total_call_alerts_purchased ?? 0;

    const entTotalWhatsappAlertsPurchased =
        enterpriseCredits?.total_whatsapp_alerts_purchased ?? 0;

    const entRemainingJobs = enterpriseCredits
        ? Math.max(0, entTotalJobsPurchased - (enterpriseCredits.used_jobs || 0))
        : Math.max(0, user?.remaining_custom_jobs ?? 0);

    const entRemainingApplications = enterpriseCredits
        ? Math.max(0, entTotalApplicationsPurchased - (enterpriseCredits.used_applications || 0))
        : Math.max(0, user?.remaining_custom_applications ?? 0);

    const entRemainingCallAlerts = Math.max(
        0,
        entTotalCallAlertsPurchased - (enterpriseCredits?.used_call_alerts || 0)
    );

    const entRemainingWhatsappAlerts = Math.max(
        0,
        entTotalWhatsappAlertsPurchased - (enterpriseCredits?.used_whatsapp_alerts || 0)
    );

    const entCompanyId =
        enterpriseCredits?.formatted_company_id || user?.formatted_company_id || "-";




    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h4"
                    sx={{
                        color: "#1E3A8A",
                        fontWeight: "bold",
                        fontSize: { xs: "1.75rem", md: "2.25rem" },
                        mb: 0.75,
                    }}
                >
                    My Subscription
                </Typography>

                <Typography
                    variant="body2"
                    sx={{
                        color: "text.secondary",
                        fontSize: "1rem",
                    }}
                >
                    View your current plan, usage, and subscription details.
                </Typography>
            </Box>

            <Paper
                elevation={3}
                sx={{
                    mb: 6,
                    p: 4,
                    borderRadius: 3,
                    background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                    border: "1px solid #e0e0e0",
                }}
            >




                {currentPlan === "FREE_TRIAL" ? (
                    <>
                        {/* FREE TRIAL UI */}

                        {showsEnterpriseCreditsBanner && (
                            <Box sx={{ mb: 4, p: 3, borderRadius: 2, bgcolor: '#EEF2FF', border: '1px solid #BFDBFE' }}>
                                <Typography variant="h6" sx={{ color: '#1E40AF', fontWeight: 'bold', mb: 1 }}>Enterprise Credits Status</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="textSecondary">Jobs: <strong>{user.remaining_custom_jobs} / {user.total_jobs_purchased}</strong></Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="textSecondary">Applications: <strong>{user.remaining_custom_applications} / {user.total_applications_purchased}</strong></Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        <Grid container spacing={3}>
                            {/* LEFT MAIN CARD */}

                            <Grid item xs={12}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        border: "1px solid #ececec",
                                        borderRadius: 3,
                                        p: 3,
                                    }}
                                >
                                    <Grid container spacing={3}>
                                        {/* LEFT SIDE */}

                                        <Grid item xs={12} md={5}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    textTransform: "uppercase",
                                                    fontWeight: "bold",
                                                    color: "#666",
                                                    mb: 1,
                                                }}
                                            >
                                                CURRENT PLAN STATUS
                                            </Typography>

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    mb: 2,
                                                }}
                                            >
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: "#6f2dbd",
                                                    }}
                                                >
                                                    Free Trial
                                                </Typography>

                                                <Chip
                                                    label={
                                                        isPlanExpired
                                                            ? "EXPIRED"
                                                            : "ACTIVE"
                                                    }
                                                    sx={{
                                                        background: isPlanExpired
                                                            ? "#eeeeee"
                                                            : "#efe3ff",

                                                        color: isPlanExpired
                                                            ? "#666"
                                                            : "#6f2dbd",
                                                        fontWeight: "bold",
                                                    }}
                                                />
                                            </Box>

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    mb: 3,
                                                    color: "#555",
                                                }}
                                            >
                                                Enjoy full access to ExiHire during your free trial period.
                                            </Typography>

                                            <Typography sx={{ mb: 1 }}>
                                                Valid Till: <strong>{new Date(expiryDate).toLocaleDateString("en-US", {
                                                month: "long",
                                                day: "numeric",
                                                year: "numeric",
                                            })}</strong>
                                            </Typography>

                                            <Typography sx={{ mb: 1 }}>
                                                Trial Started On: <strong>{user?.free_trial_started_at
                                                ? new Date(
                                                    user.free_trial_started_at
                                                ).toLocaleDateString("en-US", {
                                                    month: "long",
                                                    day: "numeric",
                                                    year: "numeric",
                                                })
                                                : new Date(
                                                    user.created_at
                                                ).toLocaleDateString("en-US", {
                                                    month: "long",
                                                    day: "numeric",
                                                    year: "numeric",
                                                })}</strong>
                                            </Typography>

                                            <Typography sx={{ mb: 3 }}>
                                                Trial Duration: <strong>15 Days</strong>
                                            </Typography>

                                            <Box sx={{ display: "flex", gap: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    sx={{
                                                        background: "#6f2dbd",
                                                    }}
                                                    onClick={() =>
                                                        (window.location.href = "/pricing")
                                                    }
                                                >
                                                    Explore Plans
                                                </Button>


                                            </Box>
                                        </Grid>

                                        {/* LIMITS */}

                                        <Grid item xs={12} md={4}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 3,
                                                    background: "#EFF6FF",
                                                    borderRadius: 3,
                                                    height: "100%",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: "#6f2dbd",
                                                        mb: 3,
                                                    }}
                                                >
                                                    Your Free Trial Limits
                                                </Typography>

                                                <Typography variant="body2">
                                                    Job Postings Remaining
                                                </Typography>

                                                <Divider sx={{ my: 1 }} />

                                                <Typography
                                                    sx={{
                                                        color: remainingJobs <= 0 ? "#d32f2f" : "#6f2dbd",
                                                        fontWeight: "bold",
                                                        mb: 3,
                                                    }}
                                                >
                                                    {remainingJobs} / {freeTrialQuota?.jobsLimit ?? jobsLimit}
                                                </Typography>

                                                {remainingJobs <= 0 && (
                                                    <Typography variant="caption" sx={{ display: 'block', color: '#d32f2f', mb: 2, fontWeight: 500 }}>
                                                        Free Trial job posting limit reached. Please upgrade your plan.
                                                    </Typography>
                                                )}

                                                <Typography variant="body2">
                                                    Candidate Applications Remaining
                                                </Typography>

                                                <Divider sx={{ my: 1 }} />

                                                <Typography
                                                    sx={{
                                                        color: remainingApplications <= 0 ? "#d32f2f" : "#6f2dbd",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    {remainingApplications} / {freeTrialQuota?.applicationsLimit ?? applicationsLimit}
                                                </Typography>

                                                {remainingApplications <= 0 && (
                                                    <Typography variant="caption" sx={{ display: 'block', color: '#d32f2f', mt: 1, fontWeight: 500 }}>
                                                        Free Trial application limit reached. Upgrade your plan.
                                                    </Typography>
                                                )}

                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: "block",
                                                        mt: 1,
                                                        color: "#777",
                                                        lineHeight: 1.6,
                                                    }}
                                                >
                                                    Free trial usage does not carry forward after upgrading.
                                                    Any unused job postings or application processing limits
                                                    from the free trial will expire once a paid plan or
                                                    Pay-As-You-Go credits are activated.
                                                </Typography>

                                            </Paper>
                                        </Grid>

                                        {/* ALERT */}

                                        <Grid item xs={12} md={3}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 3,
                                                    background: "#EFF6FF",
                                                    borderRadius: 3,
                                                    height: "100%",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: "#6f2dbd",
                                                        mb: 2,
                                                    }}
                                                >
                                                    You are on Free Trial
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        lineHeight: 2,
                                                        color: "#555",
                                                    }}
                                                >
                                                    {isPlanExpired ? (
                                                        <>
                                                            Your free trial usage has been completed.
                                                            <br />
                                                            <br />
                                                            Upgrade to continue posting jobs and processing applications.
                                                        </>
                                                    ) : (
                                                        <>
                                                            Your free trial expires in
                                                            <strong> {remainingDays} days</strong>
                                                            <br />
                                                            <br />
                                                            Upgrade anytime to continue posting jobs and accessing premium features.
                                                        </>
                                                    )}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>


                    </>
                ) : currentPlan === "PROFESSIONAL" ? (
                    <>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        border: "1px solid #ececec",
                                        borderRadius: 3,
                                        p: 3,
                                    }}
                                >
                                    <Grid container spacing={3}>

                                        {/* LEFT */}

                                        <Grid item xs={12} md={5}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    textTransform: "uppercase",
                                                    fontWeight: "bold",
                                                    color: "#666",
                                                    mb: 1,
                                                }}
                                            >
                                                CURRENT PLAN STATUS
                                            </Typography>

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    mb: 2,
                                                }}
                                            >
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: "#1E3A8A",
                                                    }}
                                                >
                                                    Professional Plan
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        py: '8px',
                                                        px: '16px',
                                                        borderRadius: '8px',
                                                        background: 'linear-gradient(90deg, #1E3A8A, #60A5FA)',
                                                        color: 'white',
                                                        boxShadow: '0 4px 10px rgba(30, 58, 138, 0.2)',
                                                        width: 'fit-content',
                                                        ml: 1
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 500,
                                                            fontSize: '0.75rem',
                                                            lineHeight: 1.4,
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        Launch Offer
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 500,
                                                            fontSize: '0.75rem',
                                                            lineHeight: 1.4,
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        Exclusive Pricing for First 50 Customers
                                                    </Typography>
                                                </Box>

                                                <Chip
                                                    label={
                                                        isPlanExpired
                                                            ? "EXPIRED"
                                                            : "ACTIVE"
                                                    }
                                                    sx={{
                                                        background: isPlanExpired
                                                            ? "#eeeeee"
                                                            : "#EEF2FF",

                                                        color: isPlanExpired
                                                            ? "#666"
                                                            : "#1E3A8A",

                                                        fontWeight: "bold",
                                                    }}
                                                />
                                            </Box>

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    mb: 3,
                                                    color: "#555",
                                                }}
                                            >
                                                For growing companies with regular hiring.
                                            </Typography>

                                            <Typography sx={{ mb: 1 }}>
                                                Valid Till:
                                                <strong>
                                                    {" "}
                                                    {expiryDate
                                                        ? new Date(expiryDate).toLocaleDateString("en-US", {
                                                            month: "long",
                                                            day: "numeric",
                                                            year: "numeric",
                                                        })
                                                        : "-"}
                                                </strong>
                                            </Typography>

                                            <Typography sx={{ mb: 1 }}>
                                                Plan Duration:
                                                <strong> 1 Year</strong>
                                            </Typography>

                                            <Typography sx={{ mb: 3 }}>
                                                Includes:
                                                <strong>
                                                    {" "}
                                                    {professionalUsage?.totalJobs || 0} Jobs + {professionalUsage?.limit || 0} Applications
                                                </strong>
                                            </Typography>

                                            <Button
                                                variant="contained"
                                                sx={{
                                                    background: "#1E3A8A",
                                                }}
                                                onClick={() =>
                                                    (window.location.href =
                                                        "/pricing")
                                                }
                                            >
                                                Upgrade Plan
                                            </Button>
                                        </Grid>

                                        {/* LIMITS */}

                                        <Grid item xs={12} md={4}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 3,
                                                    background: "#F8FAFC",
                                                    borderRadius: 3,
                                                    height: "100%",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: "#1E3A8A",
                                                        mb: 3,
                                                    }}
                                                >
                                                    Professional Plan Limits
                                                </Typography>

                                                <Typography variant="body2">
                                                    Active Job Postings
                                                </Typography>

                                                <Divider sx={{ my: 1 }} />

                                                <Typography
                                                    sx={{
                                                        color: "#1E3A8A",
                                                        fontWeight: "bold",
                                                        mb: remainingJobs <= 0 ? 1 : 3,
                                                    }}
                                                >
                                                    {remainingJobs} / {professionalUsage?.totalJobs || 0}
                                                </Typography>

                                                {remainingJobs <= 0 && (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        sx={{
                                                            mb: 3,
                                                            background: "#1E3A8A"
                                                        }}
                                                        onClick={() => window.location.href = "/pricing"}
                                                    >
                                                        Upgrade Plan
                                                    </Button>
                                                )}

                                                <Typography variant="body2">
                                                    Candidate Applications
                                                </Typography>

                                                <Divider sx={{ my: 1 }} />

                                                <Typography
                                                    sx={{
                                                        color: "#1E3A8A",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    {remainingApplications} / {professionalUsage?.limit || 0}
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: "block",
                                                        mt: 1,
                                                        color: "#777",
                                                        lineHeight: 1.6,
                                                    }}
                                                >
                                                    Professional plan limits reset after plan expiry.
                                                    Unused jobs and application processing
                                                    will not carry forward to future plans.
                                                </Typography>
                                            </Paper>
                                        </Grid>

                                        {/* RIGHT */}

                                        <Grid item xs={12} md={3}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 3,
                                                    background: "#F8FAFC",
                                                    borderRadius: 3,
                                                    height: "100%",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: "#1E3A8A",
                                                        mb: 2,
                                                    }}
                                                >
                                                    Professional Plan
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        lineHeight: 2,
                                                        color: "#555",
                                                    }}
                                                >
                                                    Your Professional subscription gives access
                                                    to {professionalUsage?.totalJobs || 0} active jobs and {professionalUsage?.limit || 0} candidate application processing credits for 1 year.


                                                </Typography>
                                            </Paper>
                                        </Grid>

                                    </Grid>
                                </Paper>
                            </Grid>


                        </Grid>
                    </>
                ) : currentPlan === "STARTER" ? (
                    <>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        border: "1px solid #ececec",
                                        borderRadius: 3,
                                        p: 3,
                                    }}
                                >
                                    <Grid container spacing={3}>

                                        {/* LEFT */}

                                        <Grid item xs={12} md={5}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    textTransform: "uppercase",
                                                    fontWeight: "bold",
                                                    color: "#666",
                                                    mb: 1,
                                                }}
                                            >
                                                CURRENT PLAN STATUS
                                            </Typography>

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    mb: 2,
                                                }}
                                            >
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: "#1E3A8A",
                                                    }}
                                                >
                                                    Starter Plan
                                                </Typography>

                                                <Chip
                                                    label={
                                                        isPlanExpired
                                                            ? "EXPIRED"
                                                            : "ACTIVE"
                                                    }
                                                    sx={{
                                                        background: isPlanExpired
                                                            ? "#eeeeee"
                                                            : "#EEF2FF",

                                                        color: isPlanExpired
                                                            ? "#666"
                                                            : "#1E3A8A",

                                                        fontWeight: "bold",
                                                    }}
                                                />
                                            </Box>

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    mb: 3,
                                                    color: "#555",
                                                }}
                                            >
                                                Perfect for startups and growing teams.
                                            </Typography>

                                            <Typography sx={{ mb: 1 }}>
                                                Valid Till:
                                                <strong>
                                                    {" "}
                                                    {expiryDate
                                                        ? new Date(expiryDate).toLocaleDateString("en-US", {
                                                            month: "long",
                                                            day: "numeric",
                                                            year: "numeric",
                                                        })
                                                        : "-"}
                                                </strong>
                                            </Typography>

                                            <Typography sx={{ mb: 1 }}>
                                                Plan Duration:
                                                <strong> 90 Days</strong>
                                            </Typography>

                                            <Typography sx={{ mb: 3 }}>
                                                Includes:
                                                <strong>
                                                    {" "}
                                                    {starterUsage?.totalJobs || 0} Jobs + {starterUsage?.limit || 0} Applications
                                                </strong>
                                            </Typography>

                                            <Button
                                                variant="contained"
                                                sx={{
                                                    background: "#1E3A8A",
                                                }}
                                                onClick={() =>
                                                    (window.location.href =
                                                        "/pricing")
                                                }
                                            >
                                                Upgrade Plan
                                            </Button>
                                        </Grid>

                                        {/* LIMITS */}

                                        <Grid item xs={12} md={4}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 3,
                                                    background: "#F8FAFC",
                                                    borderRadius: 3,
                                                    height: "100%",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: "#1E3A8A",
                                                        mb: 3,
                                                    }}
                                                >
                                                    Starter Plan Limits
                                                </Typography>

                                                <Typography variant="body2">
                                                    Active Job Postings
                                                </Typography>

                                                <Divider sx={{ my: 1 }} />

                                                <Typography
                                                    sx={{
                                                        color: "#1E3A8A",
                                                        fontWeight: "bold",
                                                        mb: remainingJobs <= 0 ? 1 : 3,
                                                    }}
                                                >
                                                    {remainingJobs} / {starterUsage?.totalJobs || 0}
                                                </Typography>

                                                {remainingJobs <= 0 && (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        sx={{
                                                            mb: 3,
                                                            background: "#1E3A8A"
                                                        }}
                                                        onClick={() => window.location.href = "/pricing"}
                                                    >
                                                        Upgrade Plan
                                                    </Button>
                                                )}

                                                <Typography variant="body2">
                                                    Candidate Applications
                                                </Typography>

                                                <Divider sx={{ my: 1 }} />

                                                <Typography
                                                    sx={{
                                                        color: "#1E3A8A",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    {remainingApplications} / {starterUsage?.limit || 0}
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: "block",
                                                        mt: 1,
                                                        color: "#777",
                                                        lineHeight: 1.6,
                                                    }}
                                                >
                                                    Starter plan limits reset after plan expiry.
                                                    Unused jobs and application processing
                                                    will not carry forward to future plans.
                                                </Typography>
                                            </Paper>
                                        </Grid>

                                        {/* RIGHT */}

                                        <Grid item xs={12} md={3}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 3,
                                                    background: "#F8FAFC",
                                                    borderRadius: 3,
                                                    height: "100%",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: "#1E3A8A",
                                                        mb: 2,
                                                    }}
                                                >
                                                    Starter Plan
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        lineHeight: 2,
                                                        color: "#555",
                                                    }}
                                                >
                                                    Your Starter subscription gives access
                                                    to {starterUsage?.totalJobs || 0} active jobs and {starterUsage?.limit || 0} candidate application processing credits for 90 days.


                                                </Typography>
                                            </Paper>
                                        </Grid>

                                    </Grid>
                                </Paper>
                            </Grid>


                        </Grid>
                    </>
                ) : currentPlan === "CUSTOM_ENTERPRISE" ? (
                    // Custom Enterprise Plan block — matches the other plans' layout
                    // (CURRENT PLAN STATUS + Upgrade Plan button) and reuses the exact
                    // same "Purchased Credits" card design/metrics as the Recruiter
                    // Dashboard. Only rendered now when Custom Enterprise is genuinely
                    // the active plan (see detection logic above).
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Paper
                                elevation={0}
                                sx={{
                                    border: "1px solid #ececec",
                                    borderRadius: 3,
                                    p: 3,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        flexDirection: { xs: "column", sm: "row" },
                                        gap: 1,
                                        mb: 1,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            textTransform: "uppercase",
                                            fontWeight: "bold",
                                            color: "#666",
                                        }}
                                    >
                                        CURRENT PLAN STATUS
                                    </Typography>

                                    <Chip
                                        label={`Company ID: ${entCompanyId}`}
                                        sx={{
                                            background: "#EEF2FF",
                                            color: "#1E3A8A",
                                            fontWeight: 600,
                                        }}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 2,
                                    }}
                                >
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: "bold",
                                            color: "#1E3A8A",
                                        }}
                                    >
                                        Custom Enterprise Plan
                                    </Typography>

                                    <Chip
                                        label="ACTIVE"
                                        sx={{
                                            background: "#EEF2FF",
                                            color: "#1E3A8A",
                                            fontWeight: "bold",
                                        }}
                                    />
                                </Box>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 3,
                                        color: "#555",
                                    }}
                                >
                                    Your custom plan is tailored to your organization's needs.
                                </Typography>

                                {/* Purchased Credits card — same layout as the Dashboard */}
                                <Box
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        background: "#EEF2FF",
                                        border: "1px solid #BFDBFE",
                                        mb: 3,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: { xs: "flex-start", sm: "center" },
                                            flexDirection: { xs: "column", sm: "row" },
                                            gap: 1,
                                            mb: 2,
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 700,
                                                color: "#1E40AF",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                            }}
                                        >
                                            <CreditCard sx={{ fontSize: 22 }} /> Purchased Credits
                                        </Typography>
                                    </Box>

                                    <Grid container spacing={4}>
                                        <Grid item xs={12} lg={6}>
                                            <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Remaining Job Credits
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {entRemainingJobs} / {entTotalJobsPurchased}
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={
                                                    entTotalJobsPurchased > 0
                                                        ? (entRemainingJobs / entTotalJobsPurchased) * 100
                                                        : 0
                                                }
                                                sx={{ height: 10, borderRadius: 5, bgcolor: "#EFF6FF", "& .MuiLinearProgress-bar": { bgcolor: "#1E3A8A" } }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} lg={6}>
                                            <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Remaining Application Credits
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {entRemainingApplications} / {entTotalApplicationsPurchased}
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={
                                                    entTotalApplicationsPurchased > 0
                                                        ? (entRemainingApplications / entTotalApplicationsPurchased) * 100
                                                        : 0
                                                }
                                                sx={{ height: 10, borderRadius: 5, bgcolor: "#EFF6FF", "& .MuiLinearProgress-bar": { bgcolor: "#10B981" } }}
                                            />
                                        </Grid>

                                        {entTotalCallAlertsPurchased > 0 && (
                                            <Grid item xs={12} lg={6}>
                                                <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        📞 Remaining Call Alerts
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {entRemainingCallAlerts} / {entTotalCallAlertsPurchased}
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={
                                                        entTotalCallAlertsPurchased > 0
                                                            ? (entRemainingCallAlerts / entTotalCallAlertsPurchased) * 100
                                                            : 0
                                                    }
                                                    sx={{ height: 10, borderRadius: 5, bgcolor: "#EFF6FF", "& .MuiLinearProgress-bar": { bgcolor: "#0284C7" } }}
                                                />
                                            </Grid>
                                        )}

                                        {entTotalWhatsappAlertsPurchased > 0 && (
                                            <Grid item xs={12} lg={6}>
                                                <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        💬 Remaining WhatsApp Alerts
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {entRemainingWhatsappAlerts} / {entTotalWhatsappAlertsPurchased}
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={
                                                        entTotalWhatsappAlertsPurchased > 0
                                                            ? (entRemainingWhatsappAlerts / entTotalWhatsappAlertsPurchased) * 100
                                                            : 0
                                                    }
                                                    sx={{ height: 10, borderRadius: 5, bgcolor: "#EFF6FF", "& .MuiLinearProgress-bar": { bgcolor: "#16A34A" } }}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>

                                <Button
                                    variant="contained"
                                    sx={{ background: "#1E3A8A" }}
                                    onClick={() => (window.location.href = "/pricing")}
                                >
                                    Upgrade Plan
                                </Button>
                            </Paper>
                        </Grid>
                    </Grid>
                ) : (
                    <Box
                        sx={{
                            textAlign: "center",
                            py: 8,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 3,
                                color: "#666",
                            }}
                        >
                            No Active Subscription Found
                        </Typography>

                        <Button
                            variant="contained"
                            onClick={() =>
                                (window.location.href = "/pricing")
                            }
                        >
                            Explore Plans
                        </Button>
                    </Box>
                )}

            </Paper>
        </Container>
    );
}

export default Subscription;